import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, uploadUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ChevronLeft, Loader2, Sparkles, Check, X, User } from "lucide-react";

/**
 * NameReview
 *
 * Admin-only review screen for the P1 "Structured Name Parsing" feature.
 * Backend regex parses each member's free-form `name` into a proposal:
 *   { title, first_name, initials, last_name, suffix }
 * Rows here are editable inline — admin can tweak any field before hitting
 * Approve. Approve-all commits every proposal as-is.
 *
 * Rules used by the parser (see `services/name_parser.py`):
 *   • Officer prefixes stay: Rtn., PDG, PP, DGE, DGN, DGND, RID
 *   • No title present → auto-prefix `Rtn.`
 *   • Professional titles (Dr., Prof., Ad., Adv., MBBS, PhD) move to suffix
 *   • Trailing single-letter tokens = initials, e.g. "Arun Kumar S L"
 *     becomes first="Arun Kumar", initials="S L", last=""
 *   • Display: `{title} {last_name}, {first_name} {initials} {suffix}`
 */
export default function NameReview() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [approvingAll, setApprovingAll] = useState(false);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState({}); // per-row busy state keyed by id

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/names/proposals");
            setItems((data.items || []).map(it => ({
                ...it,
                edited: { ...it.proposal }, // inline edits go here
            })));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load proposals");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const rerun = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.post("/admin/names/parse-all");
            toast.success(`Regenerated ${data.updated} proposals`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Regenerate failed");
        } finally {
            setRefreshing(false);
        }
    };

    const setField = (id, key, val) => {
        setItems(prev => prev.map(it =>
            it.id === id ? { ...it, edited: { ...it.edited, [key]: val } } : it
        ));
    };

    const approveOne = async (item) => {
        setBusy(prev => ({ ...prev, [item.id]: true }));
        try {
            await api.post("/admin/names/approve", { id: item.id, ...item.edited });
            toast.success("Approved", { description: item.name });
            setItems(prev => prev.filter(it => it.id !== item.id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Approve failed");
        } finally {
            setBusy(prev => ({ ...prev, [item.id]: false }));
        }
    };

    const rejectOne = async (item) => {
        setBusy(prev => ({ ...prev, [item.id]: true }));
        try {
            await api.post("/admin/names/reject", { id: item.id });
            toast.info("Dismissed", { description: item.name });
            setItems(prev => prev.filter(it => it.id !== item.id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Dismiss failed");
        } finally {
            setBusy(prev => ({ ...prev, [item.id]: false }));
        }
    };

    const approveAll = async () => {
        if (!window.confirm(`Approve all ${items.length} proposals as-is? This overwrites title/first/initials/last/suffix on every member in the list.`)) return;
        setApprovingAll(true);
        try {
            const { data } = await api.post("/admin/names/approve-all");
            toast.success(`Approved ${data.approved} members`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Approve-all failed");
        } finally {
            setApprovingAll(false);
        }
    };

    const filtered = useMemo(() => {
        if (!q.trim()) return items;
        const needle = q.trim().toLowerCase();
        return items.filter(it =>
            (it.name || "").toLowerCase().includes(needle) ||
            (it.phone || "").toLowerCase().includes(needle)
        );
    }, [items, q]);

    const preview = (parts) => {
        const { title, first_name, initials, last_name, suffix } = parts;
        const core = last_name
            ? `${last_name}, ${first_name}${initials ? ` ${initials}` : ""}`.trim()
            : `${first_name}${initials ? ` ${initials}` : ""}`.trim();
        return [title, core, suffix].filter(Boolean).join(" ");
    };

    return (
        <div className="min-h-screen bg-rotary-cream">
            <header className="border-b border-rotary-gold/20 bg-white sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Link to="/admin/platform" data-testid="name-review-back-link">
                            <Button variant="ghost" size="sm"><ChevronLeft className="h-4 w-4 mr-1" />Platform</Button>
                        </Link>
                        <h1 className="text-xl font-semibold text-rotary-blue">Structured name review</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={rerun}
                            disabled={refreshing}
                            data-testid="name-review-refresh-btn"
                        >
                            {refreshing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                            Re-parse all
                        </Button>
                        <Button
                            size="sm"
                            onClick={approveAll}
                            disabled={approvingAll || items.length === 0}
                            data-testid="name-review-approve-all-btn"
                        >
                            {approvingAll ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                            Approve all ({items.length})
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-6">
                <p className="text-sm text-slate-600 mb-4 max-w-3xl">
                    Each row shows the original name and the parser&apos;s best guess. Edit any
                    field inline, then hit Approve — the change writes to the person&apos;s
                    <code className="mx-1 text-xs bg-slate-100 px-1 rounded">title / first_name / initials / last_name / suffix</code>
                    fields. Rows are removed from the queue once approved or dismissed.
                </p>

                <div className="flex items-center gap-3 mb-4">
                    <Input
                        placeholder="Search by name or phone…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="max-w-sm"
                        data-testid="name-review-search-input"
                    />
                    <span className="text-sm text-slate-500">{filtered.length} of {items.length} shown</span>
                </div>

                {loading ? (
                    <Card className="p-10 text-center text-slate-500">
                        <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />
                        Loading proposals…
                    </Card>
                ) : filtered.length === 0 ? (
                    <Card className="p-10 text-center text-slate-500" data-testid="name-review-empty-state">
                        <Sparkles className="h-6 w-6 mx-auto mb-2 text-rotary-gold" />
                        No pending proposals. Everything has been approved (or dismissed).
                    </Card>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                        <table className="w-full text-sm" data-testid="name-review-table">
                            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-2 w-14">Photo</th>
                                    <th className="text-left px-3 py-2 min-w-[180px]">Original name</th>
                                    <th className="text-left px-3 py-2 w-20">Title</th>
                                    <th className="text-left px-3 py-2 min-w-[160px]">First name</th>
                                    <th className="text-left px-3 py-2 w-24">Initials</th>
                                    <th className="text-left px-3 py-2 w-32">Last name</th>
                                    <th className="text-left px-3 py-2 w-24">Suffix</th>
                                    <th className="text-left px-3 py-2 min-w-[220px]">Preview</th>
                                    <th className="px-3 py-2 w-40 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(it => (
                                    <tr key={it.id} className="border-t border-slate-100" data-testid={`name-review-row-${it.id}`}>
                                        <td className="px-3 py-2 align-middle">
                                            {it.photo_filename ? (
                                                <img
                                                    src={uploadUrl(it.photo_filename, it.updated_at)}
                                                    alt={it.name}
                                                    className="h-10 w-10 rounded-full object-cover bg-slate-100 border border-slate-200"
                                                    loading="lazy"
                                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                                    data-testid={`name-review-thumb-${it.id}`}
                                                />
                                            ) : (
                                                <div className="h-10 w-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400" data-testid={`name-review-thumb-placeholder-${it.id}`}>
                                                    <User className="h-5 w-5" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 align-middle">
                                            <div className="font-medium text-slate-800">{it.name}</div>
                                            <div className="text-xs text-slate-500">{it.phone}</div>
                                        </td>
                                        <td className="px-3 py-2"><Input value={it.edited.title || ""} onChange={(e) => setField(it.id, "title", e.target.value)} className="h-8" data-testid={`name-input-title-${it.id}`} /></td>
                                        <td className="px-3 py-2"><Input value={it.edited.first_name || ""} onChange={(e) => setField(it.id, "first_name", e.target.value)} className="h-8" data-testid={`name-input-first-${it.id}`} /></td>
                                        <td className="px-3 py-2"><Input value={it.edited.initials || ""} onChange={(e) => setField(it.id, "initials", e.target.value)} className="h-8" data-testid={`name-input-initials-${it.id}`} /></td>
                                        <td className="px-3 py-2"><Input value={it.edited.last_name || ""} onChange={(e) => setField(it.id, "last_name", e.target.value)} className="h-8" data-testid={`name-input-last-${it.id}`} /></td>
                                        <td className="px-3 py-2"><Input value={it.edited.suffix || ""} onChange={(e) => setField(it.id, "suffix", e.target.value)} className="h-8" data-testid={`name-input-suffix-${it.id}`} /></td>
                                        <td className="px-3 py-2 text-slate-700 font-medium">{preview(it.edited)}</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <Button
                                                size="sm"
                                                onClick={() => approveOne(it)}
                                                disabled={!!busy[it.id]}
                                                data-testid={`name-approve-${it.id}`}
                                            >
                                                <Check className="h-3.5 w-3.5 mr-1" />Approve
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => rejectOne(it)}
                                                disabled={!!busy[it.id]}
                                                data-testid={`name-reject-${it.id}`}
                                                className="ml-1"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </div>
    );
}
